import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { useLang } from "../components/LangContext";
import { STR } from "../lib/i18n";
import { api } from "../lib/api";
import { formatDate } from "../lib/categories";

export default function News() {
  const { lang } = useLang();
  const t = STR[lang];
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/news", { params: { lang } })
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [lang]);

  return (
    <div data-testid="news-page" className="min-h-screen bg-[#F6F5F0]">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 pb-24 pt-36 md:px-12 md:pt-44">
        <Reveal y={24}>
          <p className="overline-label text-[#9E643E]">{t.news.overline}</p>
          <h1 className="mt-6 font-serif text-4xl font-medium tracking-tight text-[#1A221E] sm:text-5xl lg:text-6xl">
            {t.news.title}
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-[#1A221E]/70 md:text-lg">{t.news.intro}</p>
        </Reveal>

        {loading ? (
          <p data-testid="news-loading" className="mt-20 font-serif text-2xl italic text-[#7B8A80]">
            {t.news.loading}
          </p>
        ) : articles.length === 0 ? (
          <p data-testid="news-empty" className="mt-20 font-serif text-2xl italic text-[#2D4032]">
            {t.news.empty}
          </p>
        ) : (
          <div className="mt-16">
            {articles.map((a, i) => (
              <Reveal key={a.url} delay={0.03 * (i % 4)}>
                <article
                  data-testid={`news-article-${i}`}
                  className="grid grid-cols-1 gap-4 border-t border-[#D8D3C4] py-10 last:border-b md:grid-cols-12 md:gap-8"
                >
                  <div className="md:col-span-3">
                    <p className="overline-label flex items-center gap-2 text-[#9E643E]">
                      {a.country && (
                        <span
                          data-testid={`news-country-${i}`}
                          className="inline-flex items-center rounded-full border border-[#9E643E]/40 px-2 py-0.5 text-[10px] tracking-widest"
                        >
                          {a.country}
                        </span>
                      )}
                      {a.source}
                    </p>
                    <p className="mt-3 text-xs tracking-widest text-[#7B8A80]">{formatDate(a.published, lang)}</p>
                  </div>
                  <div className="md:col-span-8">
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="group">
                      <h2 className="font-serif text-2xl font-medium leading-snug tracking-tight text-[#1A221E] transition-colors duration-300 group-hover:text-[#9E643E] md:text-3xl">
                        {a.title}
                      </h2>
                    </a>
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`news-read-more-${i}`}
                      className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#2D4032] transition-colors duration-300 hover:text-[#9E643E]"
                    >
                      {t.news.readMore} {a.source}
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
